import React, { useState, useRef, useEffect } from 'react';
import {
  SystemConfig,
  VerificationConfig,
  EmailTemplateType,
  SmsTemplateType,
  AuditLog
} from '@/types';
import {
  Save,
  CheckCircle,
  Mail,
  MessageSquare,
  FileText,
  Code,
  Type,
  Bold,
  Italic,
  Underline,
  List,
  ChevronRight,
  Smartphone,
  Send,
  Upload,
  ShieldCheck,
  User,
  Calendar,
  Activity,
  Clock,
  Loader2,
  RefreshCw,
  Trash2,
  Plus,
  Info,
  CreditCard,
  Layout,
  BookOpen,
  Fingerprint
} from 'lucide-react';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { api } from '@/services/api';
import { formatUkDateTime } from '@/lib/dates';
import { Pagination } from '@shared/components/common/Pagination';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@shared/components/ui/Table';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface SettingsTabProps {
  systemConfig?: SystemConfig;
  onUpdateSystemConfig?: (config: SystemConfig) => void;
  canEdit: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_AUDIT_FILTERS = {
  q: '',
  action: '',
  entityType: '',
  actorEmail: '',
  companyId: '',
  dateFrom: '',
  dateTo: ''
};
type AuditSortBy = 'occurredAt' | 'action' | 'entityType' | 'actorEmail' | 'companyId';
type OpayoMode = SystemConfig['opayo']['mode'];
type NavigationItem = {
  id: ConfigSubTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
};
type VerificationToggleKey =
  | 'showName'
  | 'showDate'
  | 'showRef'
  | 'showOutcome'
  | 'showExpiry'
  | 'requireNameConfirmation';
const isAuditSortBy = (value: string): value is AuditSortBy => {
  return (
    value === 'occurredAt' ||
    value === 'action' ||
    value === 'entityType' ||
    value === 'actorEmail' ||
    value === 'companyId'
  );
};
const isOpayoMode = (value: string): value is OpayoMode => value === 'test' || value === 'live';
const getAuditMetadataSummary = (metadata: AuditLog['metadata']) => {
  const safeMetadata = metadata || {};
  const keys = Object.keys(safeMetadata);
  const priorityKeys = ['source', 'reason', 'type'] as const;

  for (const key of priorityKeys) {
    const value = safeMetadata[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return keys.length > 0 ? `${keys.length} fields` : '—';
};
const toUtcBoundary = (value: string, boundary: 'start' | 'end') => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.includes('T')) return trimmed;
  const [year, month, day] = trimmed.split('-').map(Number);
  if (!year || !month || !day) return trimmed;
  const date =
    boundary === 'start'
      ? new Date(year, month - 1, day, 0, 0, 0, 0)
      : new Date(year, month - 1, day, 23, 59, 59, 999);
  return date.toISOString();
};

const ImageUploader = ({
  currentImage,
  onImageChange,
  label
}: {
  currentImage?: string;
  onImageChange: (url: string) => void;
  label: string;
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setIsUploading(true);
      try {
        const publicUrl = await api.uploadFile(file, 'Venue_Images');
        onImageChange(publicUrl);
      } catch (err: unknown) {
        showAlertModal(normalizeErrorMessage(err, 'Failed to upload image.'));
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-bold text-slate-500 uppercase">{label}</label>
      <div className="flex items-start gap-4">
        <div className="w-24 h-24 border rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden relative group">
          {currentImage ? (
            <img src={currentImage} alt="Preview" className="w-full h-full object-contain" />
          ) : (
            <span className="text-xs text-slate-400">No Logo</span>
          )}
        </div>
        <div className="flex-1">
          <label
            className={`cursor-pointer px-4 py-2 rounded border font-medium inline-flex items-center gap-2 text-sm transition-all ${isUploading ? 'bg-slate-50 text-slate-400 border-slate-200' : 'bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-400 shadow-sm'}`}
          >
            {isUploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {isUploading ? 'Uploading...' : 'Select New Logo'}
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={isUploading}
            />
          </label>
          <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">
            PNG with transparency (Max height 60px).
          </p>
        </div>
      </div>
    </div>
  );
};

const SimpleEditor = ({
  value,
  onChange,
  disabled,
  height = '700px'
}: {
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
  height?: string;
}) => {
  const [mode, setMode] = useState<'visual' | 'code'>('visual');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current && mode === 'visual' && value !== contentRef.current.innerHTML) {
      contentRef.current.innerHTML = value || '';
    }
  }, [mode, value]);

  const handleExec = (command: string, val?: string) => {
    document.execCommand(command, false, val);
    if (contentRef.current) onChange(contentRef.current.innerHTML);
  };

  return (
    <div
      className="border rounded-xl overflow-hidden bg-white flex flex-col shadow-lg border-slate-200"
      style={{ height }}
    >
      <div className="bg-slate-50 border-b p-2 flex gap-2 items-center flex-wrap sticky top-0 z-20 shadow-sm">
        <div className="flex bg-white rounded-lg border border-slate-300 overflow-hidden shadow-sm mr-2">
          <button
            onClick={() => setMode('visual')}
            className={`px-4 py-2 text-xs font-bold flex items-center gap-1.5 transition-colors ${mode === 'visual' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <Type className="w-3.5 h-3.5" /> Visual
          </button>
          <button
            onClick={() => setMode('code')}
            className={`px-4 py-2 text-xs font-bold flex items-center gap-1.5 transition-colors ${mode === 'code' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            <Code className="w-3.5 h-3.5" /> HTML
          </button>
        </div>
        {mode === 'visual' && (
          <div className="flex gap-1 bg-white p-1 rounded-lg border shadow-sm">
            <button
              onClick={() => handleExec('bold')}
              className="p-2 hover:bg-slate-100 rounded text-slate-700"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleExec('italic')}
              className="p-2 hover:bg-slate-100 rounded text-slate-700"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleExec('underline')}
              className="p-2 hover:bg-slate-100 rounded text-slate-700"
            >
              <Underline className="w-4 h-4" />
            </button>
            <div className="w-px bg-slate-200 mx-1"></div>
            <button
              onClick={() => handleExec('insertUnorderedList')}
              className="p-2 hover:bg-slate-100 rounded text-slate-700"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto relative bg-slate-100">
        {mode === 'visual' ? (
          <div className="p-10 min-h-full flex justify-center">
            <div
              ref={contentRef}
              className="w-full max-w-4xl bg-white shadow-md p-8 outline-none prose prose-sm border border-slate-200"
              contentEditable={!disabled}
              onInput={(e) => onChange(e.currentTarget.innerHTML)}
            />
          </div>
        ) : (
          <textarea
            className="w-full h-full p-6 font-mono text-sm bg-slate-900 text-teal-400 resize-none outline-none leading-relaxed"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

const VariableList = () => (
  <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
    <div className="text-xs font-bold text-teal-400 uppercase mb-3 flex items-center gap-2">
      <Code className="w-4 h-4" /> Available Variables
    </div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      {[
        { v: '{{patient_name}}', d: 'Full name' },
        { v: '{{booking_ref}}', d: 'Unique ID' },
        { v: '{{appointment_date}}', d: 'Date string' },
        { v: '{{appointment_time}}', d: 'Time string' },
        { v: '{{venue_name}}', d: 'Site name' },
        { v: '{{service_name}}', d: 'Med type' },
        { v: '{{portal_url}}', d: 'Login link' },
        { v: '{{reset_link}}', d: 'Password reset link' },
        { v: '{{company_name}}', d: 'Client name' }
      ].map((item) => (
        <div key={item.v} className="flex justify-between items-center group">
          <code
            className="text-[10px] text-teal-300 font-bold bg-white/5 px-1.5 py-0.5 rounded cursor-copy"
            onClick={() => {
              navigator.clipboard.writeText(item.v);
              showAlertModal('Copied variable!');
            }}
          >
            {item.v}
          </code>
          <span className="text-[9px] text-slate-500">{item.d}</span>
        </div>
      ))}
    </div>
  </div>
);

const MobileVerificationPreview = ({
  config,
  logoUrl
}: {
  config: VerificationConfig;
  logoUrl: string;
}) => {
  return (
    <div className="relative mx-auto border-[8px] border-slate-800 rounded-[2.5rem] h-[600px] w-[300px] bg-white shadow-2xl overflow-hidden hidden lg:block sticky top-8">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-800 rounded-b-xl z-20"></div>

      <div className="h-full w-full bg-slate-50 overflow-y-auto scrollbar-hide">
        <div className="bg-white border-b p-4 flex flex-col items-center pt-10">
          <img
            src={logoUrl || 'https://placehold.co/100x40?text=K2+OH'}
            alt="Logo"
            className="h-8 mb-4 object-contain"
          />
          <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-[10px] font-black flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> VERIFIED
          </div>
        </div>

        <div className="p-4 text-center border-b bg-white">
          <p className="text-[11px] font-medium text-slate-700">{config.validMessage}</p>
        </div>

        <div className="p-4 space-y-4">
          {config.showName && (
            <div className="flex gap-3">
              <div className="bg-blue-50 p-2 rounded-lg text-blue-600 h-fit">
                <User className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  Patient
                </p>
                <p className="font-bold text-slate-900 text-sm">Jane Smith</p>
              </div>
            </div>
          )}
          {config.showRef && (
            <div className="flex gap-3">
              <div className="bg-purple-50 p-2 rounded-lg text-purple-600 h-fit">
                <FileText className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  Certificate ID
                </p>
                <p className="font-mono text-slate-700 text-[10px] break-all">K2-CERT-992384</p>
              </div>
            </div>
          )}
          {config.showDate && (
            <div className="flex gap-3">
              <div className="bg-orange-50 p-2 rounded-lg text-orange-600 h-fit">
                <Calendar className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  Date
                </p>
                <p className="font-medium text-slate-800 text-sm">24/05/2024</p>
              </div>
            </div>
          )}
          {config.showExpiry && (
            <div className="flex gap-3">
              <div className="bg-green-50 p-2 rounded-lg text-green-600 h-fit">
                <Clock className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  Expiry
                </p>
                <p className="font-bold text-slate-800 text-sm">24/05/2027</p>
              </div>
            </div>
          )}
          {config.showOutcome && (
            <div className="flex gap-3">
              <div className="bg-teal-50 p-2 rounded-lg text-teal-600 h-fit">
                <Activity className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  Outcome
                </p>
                <p className="font-black text-teal-700 uppercase text-xs">Fit for Duties</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 text-center">
          <p className="text-[9px] text-slate-300">Verified Securely by K2 System</p>
        </div>
      </div>
    </div>
  );
};

type ConfigSubTab =
  | 'general'
  | 'communications'
  | 'payments'
  | 'email_content'
  | 'sms_content'
  | 'verification'
  | 'templates'
  | 'terms'
  | 'audit_logs';

export const SettingsTab: React.FC<SettingsTabProps> = ({
  systemConfig,
  onUpdateSystemConfig,
  canEdit
}) => {
  const [config, setConfig] = useState<SystemConfig | null>(systemConfig || null);
  const [activeSubTab, setActiveSubTab] = useState<ConfigSubTab>('general');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const readOnly = !canEdit;
  const disableInputs = readOnly && activeSubTab !== 'audit_logs';

  const [selectedEmailType, setSelectedEmailType] =
    useState<EmailTemplateType>('booking_confirmation');
  const [selectedSmsType, setSelectedSmsType] = useState<SmsTemplateType>('booking_confirmation');

  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(25);
  const [auditTotalCount, setAuditTotalCount] = useState(0);
  const [auditIsLoading, setAuditIsLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditFilters, setAuditFilters] = useState({ ...DEFAULT_AUDIT_FILTERS });
  const [auditSortBy, setAuditSortBy] = useState<
    'occurredAt' | 'action' | 'entityType' | 'actorEmail' | 'companyId'
  >('occurredAt');
  const [auditSortDir, setAuditSortDir] = useState<'asc' | 'desc'>('desc');
  const [auditRefreshToken, setAuditRefreshToken] = useState(0);

  useEffect(() => {
    if (systemConfig) {
      setConfig(systemConfig);
      setHasChanges(false);
    }
  }, [systemConfig]);

  useEffect(() => {
    if (activeSubTab !== 'audit_logs') return;
    setAuditPage(1);
  }, [activeSubTab, auditFilters, auditSortBy, auditSortDir, auditPageSize]);

  useEffect(() => {
    if (activeSubTab !== 'audit_logs') return;
    let isActive = true;
    setAuditIsLoading(true);
    setAuditError('');

    const dateFrom = auditFilters.dateFrom ? toUtcBoundary(auditFilters.dateFrom, 'start') : '';
    const dateTo = auditFilters.dateTo ? toUtcBoundary(auditFilters.dateTo, 'end') : '';

    api
      .listAuditLogs({
        page: auditPage,
        pageSize: auditPageSize,
        sortBy: auditSortBy,
        sortDir: auditSortDir,
        q: auditFilters.q || undefined,
        action: auditFilters.action || undefined,
        entityType: auditFilters.entityType || undefined,
        actorEmail: auditFilters.actorEmail || undefined,
        companyId: auditFilters.companyId || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined
      })
      .then((data) => {
        if (!isActive) return;
        setAuditLogs(data.items || []);
        setAuditTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setAuditLogs([]);
        setAuditTotalCount(0);
        setAuditError(normalizeErrorMessage(err, 'Failed to load audit logs.'));
      })
      .finally(() => {
        if (isActive) setAuditIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    activeSubTab,
    auditPage,
    auditPageSize,
    auditSortBy,
    auditSortDir,
    auditFilters,
    auditRefreshToken
  ]);

  const handleSave = async () => {
    if (!config) return;
    const hasEmptyBookingAuditEmails = bookingAuditEmails.some((email) => !email.trim());
    const hasInvalidBookingAuditEmails = bookingAuditEmails.some(
      (email) => email.trim() && !EMAIL_REGEX.test(email.trim())
    );
    if (hasEmptyBookingAuditEmails || hasInvalidBookingAuditEmails) {
      setMessage({
        type: 'error',
        text: 'Please enter valid booking audit email addresses or remove invalid rows.'
      });
      return;
    }
    setIsSaving(true);
    setMessage(null);
    try {
      if (onUpdateSystemConfig) {
        await onUpdateSystemConfig(config);
        setMessage({ type: 'success', text: 'Settings saved successfully.' });
        setHasChanges(false);
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (e: unknown) {
      setMessage({
        type: 'error',
        text: normalizeErrorMessage(e, 'Failed to save configuration.')
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateConfig = (newConfig: SystemConfig) => {
    if (readOnly) return;
    setConfig(newConfig);
    setHasChanges(true);
  };

  const handleRestoreTemplates = async (type: 'email' | 'sms' | 'cert') => {
    if (!config) return;
    const confirmed = await showConfirmModal(
      `Restore default ${type === 'cert' ? 'certificate' : type} templates? Current edits will be lost.`,
      {
        title: 'Restore Defaults',
        confirmText: 'Restore',
        cancelText: 'Cancel',
        isDestructive: true
      }
    );
    if (!confirmed) return;

    try {
      const defaults = await api.getDefaultSystemConfig();
      const newConfig = { ...config };
      if (type === 'email') {
        newConfig.templates.emailTemplates = [...(defaults.templates?.emailTemplates || [])];
      }
      if (type === 'sms') {
        newConfig.templates.smsTemplates = [...(defaults.templates?.smsTemplates || [])];
      }
      if (type === 'cert') {
        newConfig.templates.certificateTemplate = {
          ...(defaults.templates?.certificateTemplate || config.templates.certificateTemplate)
        };
      }

      updateConfig(newConfig);
    } catch (e: unknown) {
      setMessage({
        type: 'error',
        text: normalizeErrorMessage(e, 'Failed to load default templates.')
      });
    }
  };

  if (!config)
    return <div className="p-20 text-center text-slate-400">Loading system configuration...</div>;

  const emailTemplates = config.templates.emailTemplates || [];
  const smsTemplates = config.templates.smsTemplates || [];
  const bookingAuditEmails = config.templates.notificationSettings?.bookingAuditEmails || [];
  const invalidBookingAuditIndexes = new Set(
    bookingAuditEmails
      .map((email, index) => ({ email, index }))
      .filter(({ email }) => email.trim() && !EMAIL_REGEX.test(email.trim()))
      .map(({ index }) => index)
  );

  const setBookingAuditEmails = (emails: string[]) => {
    updateConfig({
      ...config,
      templates: {
        ...config.templates,
        notificationSettings: {
          ...config.templates.notificationSettings,
          bookingAuditEmails: emails
        }
      }
    });
  };
  const verificationItems: Array<{ k: VerificationToggleKey; l: string; d: string }> = [
    {
      k: 'requireNameConfirmation',
      l: 'Verify Patient Name',
      d: 'Forces manual name entry'
    },
    {
      k: 'showName',
      l: 'Display Name',
      d: 'Shows patient name after check'
    },
    {
      k: 'showDate',
      l: 'Display Visit Date',
      d: 'Shows clinical visit timestamp'
    },
    {
      k: 'showRef',
      l: 'Display Cert ID',
      d: 'Shows unique certificate UUID'
    },
    {
      k: 'showExpiry',
      l: 'Display Validity',
      d: 'Shows if expired or active'
    },
    { k: 'showOutcome', l: 'Display Outcome', d: 'Shows Fit/Unfit status' }
  ];
  const verificationConfig = config.verification as Record<VerificationToggleKey, boolean>;

  const navigationItems: NavigationItem[] = [
    { id: 'general', label: 'General Branding', icon: Layout, group: 'Application' },
    { id: 'terms', label: 'Booking Policy', icon: BookOpen, group: 'Application' },
    { id: 'audit_logs', label: 'Audit Logs', icon: Activity, group: 'Security' },
    { id: 'communications', label: 'Gateway Settings', icon: Mail, group: 'Communications' },
    { id: 'email_content', label: 'Email Templates', icon: Send, group: 'Communications' },
    { id: 'sms_content', label: 'SMS Templates', icon: MessageSquare, group: 'Communications' },
    { id: 'payments', label: 'Opayo Card Settings', icon: CreditCard, group: 'Billing' },
    { id: 'verification', label: 'Verification UI', icon: Fingerprint, group: 'Clinical Output' },
    { id: 'templates', label: 'Certificate Layout', icon: FileText, group: 'Clinical Output' }
  ];

  return (
    <div className="animate-fade-in pb-20">
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Sidebar Navigation */}
        <aside className="w-full lg:w-64 shrink-0 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-2">
            {['Application', 'Security', 'Communications', 'Billing', 'Clinical Output'].map(
              (group) => (
                <div key={group} className="mb-4 last:mb-0">
                  <p className="px-3 py-2 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                    {group}
                  </p>
                  <div className="space-y-0.5">
                    {navigationItems
                      .filter((i) => i.group === group)
                      .map((item) => (
                        <button
                          key={item.id}
                          onClick={() => setActiveSubTab(item.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-bold transition-all ${
                            activeSubTab === item.id
                              ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/20'
                              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        >
                          <item.icon
                            className={`w-4 h-4 ${activeSubTab === item.id ? 'text-white' : 'text-slate-400'}`}
                          />
                          {item.label}
                        </button>
                      ))}
                  </div>
                </div>
              )
            )}
          </div>

          {canEdit && (
            <div className="p-1">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`w-full py-4 rounded-2xl font-black flex items-center justify-center gap-3 disabled:opacity-50 shadow-xl transition-all active:scale-95 text-xs uppercase tracking-widest ${hasChanges ? 'bg-teal-600 hover:bg-teal-700 text-white animate-pulse' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? 'Saving...' : hasChanges ? 'Save Changes' : 'All Saved'}
              </button>
              {hasChanges && (
                <p className="text-[10px] text-orange-500 font-bold mt-2 text-center animate-bounce">
                  You have unsaved changes
                </p>
              )}
            </div>
          )}
        </aside>

        {/* Content Area */}
        <div className="flex-1 w-full space-y-6">
          <fieldset disabled={disableInputs} className={disableInputs ? 'opacity-70' : ''}>
            <div className="bg-slate-50/50 rounded-3xl p-1 border border-slate-100 min-h-[600px]">
              {activeSubTab === 'general' && (
                <div className="space-y-6 animate-fade-in">
                  <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-bold text-lg mb-6 text-slate-800 border-b pb-4 flex items-center gap-2">
                      <Layout className="w-5 h-5 text-teal-600" /> Branding Settings
                    </h4>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                            Application Name
                          </label>
                          <input
                            className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 font-bold"
                            value={config.general.appName}
                            onChange={(e) =>
                              updateConfig({
                                ...config,
                                general: { ...config.general, appName: e.target.value }
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                            Base App URL
                          </label>
                          <input
                            className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                            value={config.general.appUrl}
                            onChange={(e) =>
                              updateConfig({
                                ...config,
                                general: { ...config.general, appUrl: e.target.value }
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                            VAT Rate (%)
                          </label>
                          <input
                            type="number"
                            min={0}
                            step={0.01}
                            className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                            value={config.general.vatRatePercent ?? 0}
                            onChange={(e) =>
                              updateConfig({
                                ...config,
                                general: {
                                  ...config.general,
                                  vatRatePercent: Math.max(0, Number(e.target.value) || 0)
                                }
                              })
                            }
                          />
                        </div>
                      </div>
                      <ImageUploader
                        label="Primary Brand Logo"
                        currentImage={config.general.logoUrl}
                        onImageChange={(url) =>
                          updateConfig({ ...config, general: { ...config.general, logoUrl: url } })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeSubTab === 'terms' && (
                <div className="space-y-6 animate-fade-in">
                  <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-bold text-lg text-slate-800 mb-2 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-teal-600" /> Booking Policy
                    </h4>
                    <p className="text-xs text-slate-500 mb-6 font-medium">
                      This text is displayed to patients and employers during the checkout process.
                    </p>
                    <textarea
                      className="w-full border rounded-2xl p-6 h-96 bg-slate-50 font-mono text-sm shadow-inner outline-none focus:ring-2 focus:ring-teal-500"
                      value={config.termsAndConditions}
                      onChange={(e) =>
                        updateConfig({ ...config, termsAndConditions: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}

              {activeSubTab === 'audit_logs' && (
                <div className="space-y-6 animate-fade-in">
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                      <div>
                        <h4 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                          <Activity className="w-5 h-5 text-teal-600" /> Audit Logs
                        </h4>
                        <p className="text-xs text-slate-500">
                          Review system actions and changes across the platform.
                        </p>
                      </div>
                      <button
                        onClick={() => setAuditRefreshToken((prev) => prev + 1)}
                        className="px-4 py-2 text-xs font-bold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                      >
                        Refresh
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      <input
                        className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                        placeholder="Search (action, entity, actor, request)"
                        value={auditFilters.q}
                        onChange={(e) => setAuditFilters({ ...auditFilters, q: e.target.value })}
                      />
                      <input
                        className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                        placeholder="Action"
                        value={auditFilters.action}
                        onChange={(e) =>
                          setAuditFilters({ ...auditFilters, action: e.target.value })
                        }
                      />
                      <input
                        className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                        placeholder="Entity Type"
                        value={auditFilters.entityType}
                        onChange={(e) =>
                          setAuditFilters({ ...auditFilters, entityType: e.target.value })
                        }
                      />
                      <input
                        className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                        placeholder="Actor Email"
                        value={auditFilters.actorEmail}
                        onChange={(e) =>
                          setAuditFilters({ ...auditFilters, actorEmail: e.target.value })
                        }
                      />
                      <input
                        className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                        placeholder="Company ID"
                        value={auditFilters.companyId}
                        onChange={(e) =>
                          setAuditFilters({ ...auditFilters, companyId: e.target.value })
                        }
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                          value={auditFilters.dateFrom}
                          onChange={(e) =>
                            setAuditFilters({ ...auditFilters, dateFrom: e.target.value })
                          }
                        />
                        <input
                          type="date"
                          className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                          value={auditFilters.dateTo}
                          onChange={(e) =>
                            setAuditFilters({ ...auditFilters, dateTo: e.target.value })
                          }
                        />
                      </div>
                      <select
                        className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                        value={auditSortBy}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (isAuditSortBy(value)) {
                            setAuditSortBy(value);
                          }
                        }}
                      >
                        <option value="occurredAt">Sort: Date/Time</option>
                        <option value="action">Sort: Action</option>
                        <option value="entityType">Sort: Entity Type</option>
                        <option value="actorEmail">Sort: Actor Email</option>
                        <option value="companyId">Sort: Company ID</option>
                      </select>
                      <select
                        className="w-full border rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
                        value={auditSortDir}
                        onChange={(e) => setAuditSortDir(e.target.value as 'asc' | 'desc')}
                      >
                        <option value="desc">Newest First</option>
                        <option value="asc">Oldest First</option>
                      </select>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
                      <button
                        onClick={() => setAuditFilters({ ...DEFAULT_AUDIT_FILTERS })}
                        className="text-xs font-bold text-slate-500 hover:text-slate-700"
                      >
                        Clear Filters
                      </button>
                      <p className="text-xs text-slate-500">
                        Total Records: <span className="font-semibold">{auditTotalCount}</span>
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <Table className="w-full text-left">
                        <THead className="bg-slate-50 border-b">
                          <TR>
                            <TH>Date</TH>
                            <TH>Action</TH>
                            <TH>Entity</TH>
                            <TH>Actor</TH>
                            <TH>Company</TH>
                            <TH>Changes</TH>
                            <TH>Metadata</TH>
                          </TR>
                        </THead>
                        <TBody className="divide-y divide-slate-100">
                          {auditIsLoading ? (
                            <EmptyRow colSpan={7} className="p-8 text-center text-slate-500">
                              Loading audit logs...
                            </EmptyRow>
                          ) : auditError ? (
                            <EmptyRow colSpan={7} className="p-8 text-center text-red-500">
                              {auditError}
                            </EmptyRow>
                          ) : auditLogs.length === 0 ? (
                            <EmptyRow colSpan={7} className="p-8 text-center text-slate-500">
                              No audit logs found.
                            </EmptyRow>
                          ) : (
                            auditLogs.map((log) => {
                              const occurredAt = log.occurredAt
                                ? formatUkDateTime(log.occurredAt, '—')
                                : '—';
                              const actorPrimary = log.actorEmail || log.actorId || '—';
                              const actorSecondary = log.actorType || '—';
                              const changeKeys = Object.keys(log.changes || {});
                              const changeSummary =
                                changeKeys.length === 0 ? '—' : `${changeKeys.length} fields`;
                              const changePreview =
                                changeKeys.length > 0
                                  ? changeKeys.slice(0, 2).join(', ') +
                                    (changeKeys.length > 2 ? ` +${changeKeys.length - 2}` : '')
                                  : '';
                              const metadataSummary = getAuditMetadataSummary(log.metadata);

                              return (
                                <TR key={log.id} className="hover:bg-slate-50">
                                  <TD className="text-xs text-slate-600">{occurredAt}</TD>
                                  <TD className="text-xs font-bold text-slate-800">
                                    {log.action}
                                    {log.requestId && (
                                      <div className="text-[10px] text-slate-400 mt-1">
                                        Req: {log.requestId}
                                      </div>
                                    )}
                                  </TD>
                                  <TD className="text-xs text-slate-700">
                                    <div className="font-semibold">{log.entityType}</div>
                                    <div className="text-[10px] text-slate-400">
                                      {log.entityId || '—'}
                                    </div>
                                  </TD>
                                  <TD className="text-xs text-slate-700">
                                    <div className="font-semibold">{actorPrimary}</div>
                                    <div className="text-[10px] text-slate-400">
                                      {actorSecondary}
                                    </div>
                                  </TD>
                                  <TD className="text-xs text-slate-600">{log.companyId || '—'}</TD>
                                  <TD className="text-xs text-slate-600">
                                    <div className="font-semibold">{changeSummary}</div>
                                    {changePreview && (
                                      <div className="text-[10px] text-slate-400">
                                        {changePreview}
                                      </div>
                                    )}
                                  </TD>
                                  <TD className="text-xs text-slate-600">{metadataSummary}</TD>
                                </TR>
                              );
                            })
                          )}
                        </TBody>
                      </Table>
                    </div>
                    <div className="p-4">
                      <Pagination
                        page={auditPage}
                        pageSize={auditPageSize}
                        totalCount={auditTotalCount}
                        onPageChange={setAuditPage}
                        onPageSizeChange={setAuditPageSize}
                        isLoading={auditIsLoading}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeSubTab === 'communications' && (
                <div className="space-y-8 animate-fade-in">
                  <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-bold text-lg mb-6 text-slate-800 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-600" /> SMTP Gateway
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-2">Host</label>
                        <input
                          className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                          value={config.smtp.host}
                          onChange={(e) =>
                            updateConfig({
                              ...config,
                              smtp: { ...config.smtp, host: e.target.value }
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-2">Port</label>
                        <input
                          type="number"
                          className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                          value={config.smtp.port}
                          onChange={(e) =>
                            updateConfig({
                              ...config,
                              smtp: { ...config.smtp, port: Number(e.target.value) }
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-2">
                          User / Email
                        </label>
                        <input
                          className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                          value={config.smtp.user}
                          onChange={(e) =>
                            updateConfig({
                              ...config,
                              smtp: { ...config.smtp, user: e.target.value }
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                          value={config.smtp.pass}
                          onChange={(e) =>
                            updateConfig({
                              ...config,
                              smtp: { ...config.smtp, pass: e.target.value }
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-bold text-lg mb-6 text-slate-800 flex items-center gap-2">
                      <Smartphone className="w-5 h-5 text-green-600" /> Twilio SMS Gateway
                    </h4>
                    <div className="space-y-6">
                      <label className="flex items-center gap-3 cursor-pointer p-4 border rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                        <input
                          type="checkbox"
                          checked={config.twilio.enabled}
                          onChange={(e) =>
                            updateConfig({
                              ...config,
                              twilio: { ...config.twilio, enabled: e.target.checked }
                            })
                          }
                          className="w-5 h-5 text-teal-600 rounded"
                        />
                        <div>
                          <span className="font-bold text-slate-700 block">
                            Enable Automated SMS
                          </span>
                          <span className="text-[10px] text-slate-500 font-medium uppercase tracking-tight">
                            Requires valid Twilio credentials below
                          </span>
                        </div>
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-xs font-bold text-slate-500 mb-2">
                            Account SID
                          </label>
                          <input
                            className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                            value={config.twilio.accountSid}
                            onChange={(e) =>
                              updateConfig({
                                ...config,
                                twilio: { ...config.twilio, accountSid: e.target.value }
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-500 mb-2">
                            Auth Token
                          </label>
                          <input
                            type="password"
                            className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                            value={config.twilio.authToken}
                            onChange={(e) =>
                              updateConfig({
                                ...config,
                                twilio: { ...config.twilio, authToken: e.target.value }
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="font-bold text-lg mb-3 text-slate-800 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-teal-600" /> Booking Audit Email Recipients
                    </h4>
                    <p className="text-xs text-slate-500 mb-4">
                      New booking confirmations will also be emailed to these addresses.
                    </p>
                    <div className="space-y-3">
                      {(bookingAuditEmails.length > 0 ? bookingAuditEmails : ['']).map(
                        (email, index) => (
                          <div key={`booking-audit-${index}`} className="flex items-center gap-2">
                            <input
                              type="email"
                              placeholder="sammy@k2a.uk.com"
                              className={`flex-1 border rounded-xl p-3 bg-slate-50 font-mono text-sm outline-none focus:ring-2 ${
                                invalidBookingAuditIndexes.has(index)
                                  ? 'border-red-300 focus:ring-red-500'
                                  : 'focus:ring-teal-500'
                              }`}
                              value={email}
                              onChange={(e) => {
                                const next = [...bookingAuditEmails];
                                next[index] = e.target.value.trim().toLowerCase();
                                setBookingAuditEmails(next);
                              }}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setBookingAuditEmails(
                                  bookingAuditEmails.filter((_, emailIndex) => emailIndex !== index)
                                )
                              }
                              className="p-2.5 rounded-lg border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all"
                              title="Remove email"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const hasEmpty = bookingAuditEmails.some((email) => !email.trim());
                          const hasInvalid = bookingAuditEmails.some(
                            (email) => email.trim() && !EMAIL_REGEX.test(email.trim())
                          );
                          if (hasEmpty || hasInvalid) {
                            setMessage({
                              type: 'error',
                              text: 'Fix invalid email rows before adding another recipient.'
                            });
                            return;
                          }
                          setBookingAuditEmails([...bookingAuditEmails, '']);
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-teal-200 bg-teal-50 text-teal-700 text-xs font-bold hover:bg-teal-100 transition-all"
                      >
                        <Plus className="w-4 h-4" />
                        Add Recipient
                      </button>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">Add one recipient per row.</p>
                    {invalidBookingAuditIndexes.size > 0 && (
                      <p className="text-[10px] text-red-500 mt-1">
                        One or more email addresses are invalid.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {activeSubTab === 'email_content' && (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 animate-fade-in">
                  <div className="space-y-1 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm h-fit">
                    <p className="px-3 py-2 text-[10px] font-black uppercase text-slate-400">
                      Trigger Events
                    </p>
                    {emailTemplates.length === 0 ? (
                      <p className="text-xs text-slate-400 italic px-3">No templates found.</p>
                    ) : (
                      emailTemplates.map((t) => (
                        <button
                          key={t.type}
                          onClick={() => setSelectedEmailType(t.type)}
                          className={`w-full text-left p-3 rounded-xl transition-all border-2 text-xs font-bold flex items-center justify-between group ${selectedEmailType === t.type ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-transparent bg-white text-slate-500 hover:bg-slate-50'}`}
                        >
                          {t.label}
                          <ChevronRight
                            className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${selectedEmailType === t.type ? 'opacity-100' : ''}`}
                          />
                        </button>
                      ))
                    )}
                  </div>

                  <div className="lg:col-span-3 space-y-6">
                    {(() => {
                      const template = emailTemplates.find((t) => t.type === selectedEmailType);
                      if (!template)
                        return (
                          <div className="p-12 text-center text-slate-400">
                            Select a trigger from the left
                          </div>
                        );

                      return (
                        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                          <div className="flex justify-between items-center border-b pb-4">
                            <h4 className="font-bold text-lg text-slate-800">
                              Editing: {template.label}
                            </h4>
                            <VariableList />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                              Subject Line
                            </label>
                            <input
                              className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 font-bold"
                              value={template.subject}
                              onChange={(e) => {
                                const newTemplates = [...emailTemplates];
                                const idx = newTemplates.findIndex(
                                  (x) => x.type === selectedEmailType
                                );
                                newTemplates[idx] = { ...template, subject: e.target.value };
                                updateConfig({
                                  ...config,
                                  templates: { ...config.templates, emailTemplates: newTemplates }
                                });
                              }}
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                              Message Body
                            </label>
                            <SimpleEditor
                              height="400px"
                              value={template.body}
                              onChange={(val) => {
                                const newTemplates = [...emailTemplates];
                                const idx = newTemplates.findIndex(
                                  (x) => x.type === selectedEmailType
                                );
                                newTemplates[idx] = { ...template, body: val };
                                updateConfig({
                                  ...config,
                                  templates: { ...config.templates, emailTemplates: newTemplates }
                                });
                              }}
                              disabled={!canEdit}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {activeSubTab === 'sms_content' && (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 animate-fade-in">
                  <div className="space-y-1 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm h-fit">
                    <p className="px-3 py-2 text-[10px] font-black uppercase text-slate-400">
                      Trigger Events
                    </p>
                    {smsTemplates.length === 0 ? (
                      <p className="text-xs text-slate-400 italic px-3">No templates found.</p>
                    ) : (
                      smsTemplates.map((t) => (
                        <button
                          key={t.type}
                          onClick={() => setSelectedSmsType(t.type)}
                          className={`w-full text-left p-3 rounded-xl transition-all border-2 text-xs font-bold flex items-center justify-between group ${selectedSmsType === t.type ? 'border-teal-500 bg-teal-50 text-teal-800' : 'border-transparent bg-white text-slate-500 hover:bg-slate-50'}`}
                        >
                          {t.label}
                          <ChevronRight
                            className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${selectedSmsType === t.type ? 'opacity-100' : ''}`}
                          />
                        </button>
                      ))
                    )}
                  </div>

                  <div className="lg:col-span-3 space-y-6">
                    {(() => {
                      const template = smsTemplates.find((t) => t.type === selectedSmsType);
                      if (!template)
                        return (
                          <div className="p-12 text-center text-slate-400">
                            Select a trigger from the left
                          </div>
                        );

                      return (
                        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                          <div className="flex justify-between items-center border-b pb-4">
                            <h4 className="font-bold text-lg text-slate-800">
                              Edit SMS: {template.label}
                            </h4>
                            <VariableList />
                          </div>

                          <div className="relative">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                              SMS Body (Plain Text)
                            </label>
                            <textarea
                              className="w-full border rounded-xl p-4 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm leading-relaxed"
                              rows={6}
                              maxLength={160}
                              value={template.body}
                              onChange={(e) => {
                                const newTemplates = [...smsTemplates];
                                const idx = newTemplates.findIndex(
                                  (x) => x.type === selectedSmsType
                                );
                                newTemplates[idx] = { ...template, body: e.target.value };
                                updateConfig({
                                  ...config,
                                  templates: { ...config.templates, smsTemplates: newTemplates }
                                });
                              }}
                            />
                            <div
                              className={`absolute bottom-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded ${template.body.length > 140 ? 'bg-orange-100 text-orange-600' : 'bg-slate-200 text-slate-500'}`}
                            >
                              {template.body.length} / 160
                            </div>
                          </div>
                          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3 items-center">
                            <Info className="w-5 h-5 text-blue-600" />
                            <p className="text-xs text-blue-700">
                              SMS variables will be replaced automatically. Ensure content is under
                              160 characters to avoid multiple segment costs.
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {activeSubTab === 'payments' && (
                <div className="space-y-8 animate-fade-in">
                  <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex justify-between items-center mb-6 border-b pb-4">
                      <h4 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-indigo-600" /> Opayo Pi Integration
                      </h4>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <span className="text-xs font-bold text-slate-500 uppercase">
                          Integration Active
                        </span>
                        <input
                          type="checkbox"
                          checked={config.opayo.enabled}
                          onChange={(e) =>
                            updateConfig({
                              ...config,
                              opayo: { ...config.opayo, enabled: e.target.checked }
                            })
                          }
                          className="w-6 h-6 text-teal-600 rounded-lg border-slate-300"
                        />
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                          Vendor Name
                        </label>
                        <input
                          className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                          value={config.opayo.vendorName}
                          onChange={(e) =>
                            updateConfig({
                              ...config,
                              opayo: { ...config.opayo, vendorName: e.target.value }
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                          Environment
                        </label>
                        <select
                          className="w-full border rounded-xl p-3 bg-slate-50 text-sm font-bold"
                          value={config.opayo.mode}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (isOpayoMode(value)) {
                              updateConfig({
                                ...config,
                                opayo: { ...config.opayo, mode: value }
                              });
                            }
                          }}
                        >
                          <option value="test">Sandbox (Development)</option>
                          <option value="live">Production (Live)</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                          Integration Key
                        </label>
                        <input
                          type="password"
                          placeholder="••••••••••••••••"
                          className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                          value={config.opayo.integrationKey}
                          onChange={(e) =>
                            updateConfig({
                              ...config,
                              opayo: { ...config.opayo, integrationKey: e.target.value }
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                          Integration Password
                        </label>
                        <input
                          type="password"
                          placeholder="••••••••••••••••"
                          className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
                          value={config.opayo.integrationPassword}
                          onChange={(e) =>
                            updateConfig({
                              ...config,
                              opayo: { ...config.opayo, integrationPassword: e.target.value }
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSubTab === 'verification' && (
                <div className="flex flex-col lg:flex-row gap-12 animate-fade-in p-2">
                  <div className="flex-1 space-y-6">
                    <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                      <h4 className="font-bold text-lg mb-2 text-slate-800 flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-teal-600" /> Verification Engine
                      </h4>
                      <p className="text-xs text-slate-500 mb-8 font-medium">
                        Control the data patients and third-parties see when scanning certificate QR
                        codes.
                      </p>

                      <div className="space-y-6">
                        <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                            Default Success Message
                          </label>
                          <textarea
                            rows={2}
                            className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
                            value={config.verification.validMessage}
                            onChange={(e) =>
                              updateConfig({
                                ...config,
                                verification: {
                                  ...config.verification,
                                  validMessage: e.target.value
                                }
                              })
                            }
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {verificationItems.map((item) => (
                            <label
                              key={item.k}
                              className={`flex items-start gap-3 p-4 border rounded-2xl transition-all cursor-pointer group ${verificationConfig[item.k] ? 'bg-teal-50 border-teal-200' : 'bg-white hover:bg-slate-50'}`}
                            >
                              <div className="pt-0.5">
                                <input
                                  type="checkbox"
                                  className="w-5 h-5 text-teal-600 rounded"
                                  checked={verificationConfig[item.k]}
                                  onChange={(e) =>
                                    updateConfig({
                                      ...config,
                                      verification: {
                                        ...config.verification,
                                        [item.k]: e.target.checked
                                      }
                                    })
                                  }
                                />
                              </div>
                              <div>
                                <span className="block text-xs font-black text-slate-800 uppercase tracking-tight">
                                  {item.l}
                                </span>
                                <span className="block text-[10px] text-slate-400 mt-0.5 font-medium">
                                  {item.d}
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-full lg:w-[320px]">
                    <div className="bg-slate-900/5 p-4 rounded-3xl border-2 border-dashed border-slate-200 flex flex-col items-center">
                      <div className="flex items-center gap-2 mb-4 text-slate-400">
                        <Smartphone className="w-4 h-4" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                          Patient View
                        </span>
                      </div>
                      <MobileVerificationPreview
                        config={config.verification}
                        logoUrl={config.general.logoUrl || ''}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeSubTab === 'templates' && (
                <div className="animate-fade-in p-2">
                  <div className="mb-6 flex justify-between items-end">
                    <div>
                      <h4 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-teal-600" /> Certificate Template
                      </h4>
                      <p className="text-xs text-slate-500 font-medium">
                        Use HTML to customize the appearance of medical outcomes.
                      </p>
                    </div>
                    <button
                      onClick={() => handleRestoreTemplates('cert')}
                      className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold hover:bg-slate-50 flex items-center gap-2 shadow-sm transition-all"
                    >
                      <RefreshCw className="w-3 h-3" /> Restore Default HTML
                    </button>
                  </div>
                  <SimpleEditor
                    value={config.templates.certificateTemplate.body}
                    onChange={(val) =>
                      updateConfig({
                        ...config,
                        templates: {
                          ...config.templates,
                          certificateTemplate: {
                            ...config.templates.certificateTemplate,
                            body: val
                          }
                        }
                      })
                    }
                    disabled={!canEdit}
                  />
                </div>
              )}
            </div>
          </fieldset>
        </div>
      </div>
    </div>
  );
};
